import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/i18n";
import { Wordmark } from "@/components/Monogram";
import { Reveal, Eyebrow } from "@/components/Reveal";

const T = {
  it: {
    eyebrow: "Documenti legali",
    terminiTitle: "Termini e Condizioni di Servizio NCC",
    terminiIntro: "Informazioni complete di servizio. Leggi le condizioni e i termini di servizio prima della partenza.",
    privacyTitle: "Privacy Policy",
    privacyIntro: "Informativa ai sensi degli artt. 13-14 del Regolamento UE 2016/679 (GDPR).",
    cookiesTitle: "Cookie Policy",
    cookiesIntro: "Come utilizziamo i cookie e come puoi gestire le tue preferenze.",
    back: "Torna al sito",
  },
  en: {
    eyebrow: "Legal documents",
    terminiTitle: "NCC Service Terms & Conditions",
    terminiIntro: "Complete service information. Please read our conditions and terms of service before departure.",
    privacyTitle: "Privacy Policy",
    privacyIntro: "Notice pursuant to arts. 13-14 of EU Regulation 2016/679 (GDPR).",
    cookiesTitle: "Cookie Policy",
    cookiesIntro: "How we use cookies and how you can manage your preferences.",
    back: "Back to the website",
  },
};

const CONDITIONS = [
  {
    title: { it: "1. Oggetto e ambito del servizio", en: "1. Scope of the service" },
    body: {
      it: "Obsidian Private Driver fornisce servizi di noleggio con conducente (NCC) esclusivamente su prenotazione, mediante Mercedes 4MATIC omologato per un massimo di 6 passeggeri oltre al conducente. Il servizio viene svolto secondo itinerari, orari e condizioni concordati al momento della conferma.",
      en: "Obsidian Private Driver provides chauffeur-driven car hire (NCC) services exclusively by reservation, using a Mercedes 4MATIC approved for a maximum of 6 passengers in addition to the driver. The service is carried out according to itineraries, times and conditions agreed at the time of confirmation.",
    },
  },
  {
    title: { it: "2. Richiesta e conferma della prenotazione", en: "2. Request and confirmation of the booking" },
    body: {
      it: "L'invio di una richiesta tramite sito, email, telefono o WhatsApp non costituisce automaticamente una prenotazione. Il servizio si considera confermato solo dopo l'accettazione espressa da parte di Obsidian Private Driver e, quando richiesto, dopo il versamento della caparra o del saldo anticipato indicato nel preventivo. Il cliente è tenuto a fornire informazioni complete e corrette riguardo a data, orario, partenza, destinazione, numero dei passeggeri, bagagli ed eventuali esigenze particolari.",
      en: "Submitting a request via website, email, telephone or WhatsApp does not automatically constitute a booking. The service is deemed confirmed only after express acceptance by Obsidian Private Driver and, where required, after payment of the deposit or advance balance indicated in the quote. The client must provide complete and correct information regarding date, time, departure, destination, number of passengers, luggage and any special requirements.",
    },
  },
  {
    title: { it: "3. Tariffe, supplementi e pagamenti", en: "3. Fares, supplements and payments" },
    body: {
      it: "La tariffa viene comunicata prima della conferma e comprende il servizio del conducente, il carburante e i pedaggi autostradali ordinari relativi all'itinerario concordato, salvo diversa indicazione nel preventivo. Parcheggi, ZTL, trafori, traghetti, pedaggi esteri, vitto o pernottamento del conducente, attese, soste e deviazioni sono inclusi soltanto se espressamente indicati. Per i servizi svolti tra le 22:00 e le 06:00 si applica una maggiorazione del 20% sulla tariffa base; per i servizi urbani minimi un supplemento fisso di 5,00 euro. Nelle giornate festive si applica una maggiorazione del 20%. Sono accettati: carte Visa, Mastercard, Maestro, V Pay, American Express, UnionPay, JCB e Bancontact tramite terminale MyPOS Ultra; Apple Pay, Google Pay e Samsung Pay; Satispay; bonifico bancario istantaneo; contanti nei limiti previsti dalla normativa vigente. I pagamenti a distanza o anticipati devono risultare contabilizzati entro il termine comunicato nella conferma.",
      en: "The fare is communicated before confirmation and includes the driver's service, fuel and ordinary motorway tolls relating to the agreed itinerary, unless otherwise stated in the quote. Parking, ZTL areas, tunnels, ferries, foreign tolls, driver's meals or accommodation, waiting, stops and detours are included only if expressly indicated. A 20% surcharge applies to services carried out between 10:00 pm and 6:00 am; a fixed supplement of €5.00 applies to minimum urban services. A 20% surcharge applies on public holidays. Accepted: Visa, Mastercard, Maestro, V Pay, American Express, UnionPay, JCB and Bancontact cards via MyPOS Ultra terminal; Apple Pay, Google Pay and Samsung Pay; Satispay; instant bank transfer; cash within the limits set by applicable law. Remote or advance payments must be accounted for within the deadline communicated in the confirmation.",
    },
  },
  {
    title: { it: "4. Cancellazioni e no-show", en: "4. Cancellations and no-show" },
    body: {
      it: "La cancellazione comunicata almeno 24 ore prima dell'orario di partenza concordato non comporta penali. Per cancellazioni comunicate con meno di 24 ore di preavviso viene addebitato il 50% dell'importo totale. In caso di no-show, cioè mancata presentazione senza preavviso, viene addebitato il 100% dell'importo concordato.",
      en: "Cancellation communicated at least 24 hours before the agreed departure time does not incur any penalty. For cancellations communicated with less than 24 hours' notice, 50% of the total amount is charged. In case of no-show, i.e. failure to show up without notice, 100% of the agreed amount is charged.",
    },
  },
  {
    title: { it: "5. Tempi di attesa e ritardi", en: "5. Waiting times and delays" },
    body: {
      it: "La tariffa comprende 15 minuti di attesa per prelievi presso hotel, abitazioni o altri punti concordati e 60 minuti per i prelievi aeroportuali, calcolati dall'orario effettivo di atterraggio del volo. Oltre il periodo incluso si applica la tariffa di attesa comunicata nel preventivo, anche in modo frazionato. Il cliente deve informare tempestivamente Obsidian di ritardi o variazioni note. Per stazioni ferroviarie e altri nodi di trasporto, il tempo di attesa incluso deve essere indicato nel preventivo.",
      en: "The fare includes 15 minutes of waiting for pick-ups at hotels, residences or other agreed points and 60 minutes for airport pick-ups, calculated from the flight's actual landing time. Beyond the included period, the waiting rate communicated in the quote applies, also on a fractional basis. The client must promptly inform Obsidian of any known delays or changes. For railway stations and other transport hubs, the included waiting time must be stated in the quote.",
    },
  },
  {
    title: { it: "6. Bagagli e attrezzatura", en: "6. Luggage and equipment" },
    body: {
      it: "Ogni passeggero può trasportare bagagli standard compatibilmente con la capienza effettiva del veicolo. La presenza di bagagli fuori misura, sci, biciclette o altra attrezzatura deve essere comunicata prima della conferma. Per ragioni di sicurezza, il conducente può rifiutare oggetti non dichiarati che non possano essere sistemati correttamente o che compromettano la sicurezza del viaggio.",
      en: "Each passenger may carry standard luggage within the actual capacity of the vehicle. The presence of oversized luggage, skis, bicycles or other equipment must be communicated before confirmation. For safety reasons, the driver may refuse undeclared items that cannot be properly stowed or that would compromise the safety of the journey.",
    },
  },
  {
    title: { it: "7. Bambini e sistemi di ritenuta", en: "7. Children and child restraints" },
    body: {
      it: "La presenza di bambini e l'eventuale necessità di seggiolini o rialzi devono essere segnalate nella richiesta, indicando età, altezza e peso. La disponibilità del dispositivo e le eventuali condizioni economiche saranno confermate prima del servizio.",
      en: "The presence of children and any need for child seats or boosters must be reported in the request, indicating age, height and weight. Availability of the device and any economic conditions will be confirmed before the service.",
    },
  },
  {
    title: { it: "8. Animali e cani di assistenza", en: "8. Pets and assistance dogs" },
    body: {
      it: "Il trasporto di animali domestici è consentito previa autorizzazione. Salvo diverso accordo, sono ammessi animali di piccola taglia all'interno di un trasportino idoneo. I cani guida e gli altri cani di assistenza riconosciuti sono gestiti nel rispetto della normativa applicabile; la loro presenza deve essere comunicata per consentire la corretta organizzazione del viaggio.",
      en: "Transport of pets is allowed subject to prior authorisation. Unless otherwise agreed, small pets are accepted inside a suitable carrier. Guide dogs and other recognised assistance dogs are handled in compliance with applicable law; their presence must be communicated to allow correct organisation of the journey.",
    },
  },
  {
    title: { it: "9. Regole a bordo", en: "9. On-board rules" },
    body: {
      it: "Tutti i passeggeri devono utilizzare le cinture di sicurezza per l'intera durata del viaggio. È vietato fumare, incluse sigarette elettroniche e dispositivi a tabacco riscaldato. Il consumo di cibi è sconsigliato; eventuali bevande e alimenti devono essere gestiti con attenzione. I passeggeri devono rispettare il veicolo, le dotazioni e le indicazioni del conducente relative alla sicurezza.",
      en: "All passengers must wear seat belts for the entire duration of the journey. Smoking is prohibited, including electronic cigarettes and heated tobacco devices. Eating is not recommended; any beverages and food must be handled with care. Passengers must respect the vehicle, its fittings and the driver's safety instructions.",
    },
  },
  {
    title: { it: "10. Danni e pulizia straordinaria", en: "10. Damage and extraordinary cleaning" },
    body: {
      it: "Il cliente è responsabile dei danni direttamente causati da sé o dai passeggeri inclusi nella prenotazione. Qualora si renda necessaria una pulizia straordinaria a causa di liquidi, vomito, fango eccessivo o altra incuria, può essere addebitato un costo minimo di 150,00 euro, oltre agli eventuali costi documentati per il ripristino dei danni.",
      en: "The client is responsible for damage directly caused by themselves or by the passengers included in the booking. Should extraordinary cleaning become necessary due to liquids, vomiting, excessive mud or other negligence, a minimum charge of €150.00 may apply, in addition to any documented costs for repairing the damage.",
    },
  },
  {
    title: { it: "11. Deviazioni, soste e variazioni", en: "11. Detours, stops and changes" },
    body: {
      it: "Variazioni del percorso, fermate intermedie, prelievo di ulteriori passeggeri o soste non concordate sono soggette alla disponibilità del conducente e possono comportare un costo aggiuntivo, calcolato in base al tempo e alla distanza. Il costo deve essere comunicato e accettato prima della variazione, quando possibile.",
      en: "Route changes, intermediate stops, picking up additional passengers or unagreed stops are subject to the driver's availability and may incur an additional cost, calculated on time and distance. Where possible, the cost must be communicated and accepted before the change.",
    },
  },
  {
    title: { it: "12. Viabilità ed eventi non imputabili al vettore", en: "12. Traffic and events beyond the carrier's control" },
    body: {
      it: "Obsidian si impegna a pianificare il servizio e rispettare gli orari concordati. Tempi di percorrenza e arrivo possono tuttavia variare per traffico eccezionale, chiusure stradali, incidenti, provvedimenti delle autorità o condizioni meteorologiche avverse. Il cliente deve comunicare eventuali coincidenze o orari inderogabili affinché possa essere suggerito un margine adeguato. L'eventuale limitazione di responsabilità per collegamenti persi deve rispettare la normativa applicabile.",
      en: "Obsidian undertakes to plan the service and respect the agreed times. Travel and arrival times may nevertheless vary due to exceptional traffic, road closures, accidents, authority measures or adverse weather conditions. The client must communicate any connections or non-negotiable times so that an adequate margin can be suggested. Any limitation of liability for missed connections must comply with applicable law.",
    },
  },
  {
    title: { it: "13. Reclami, legge applicabile e controversie", en: "13. Complaints, applicable law and disputes" },
    body: {
      it: "Eventuali reclami possono essere inviati all'indirizzo email o PEC indicato nel footer, indicando data del servizio e riferimenti della prenotazione. Il contratto è regolato dalla legge italiana. Le informazioni relative a foro competente e strumenti di risoluzione delle controversie sono definite nel rispetto del Codice del Consumo e della normativa applicabile.",
      en: "Complaints may be sent to the email or PEC address indicated in the footer, stating the service date and booking references. The contract is governed by Italian law. Information on the competent court and dispute resolution methods is defined in compliance with the Consumer Code and applicable law.",
    },
  },
  {
    title: { it: "14. Dati del prestatore", en: "14. Service provider details" },
    body: {
      it: "OBSIDIAN TAXI di Rech Nicky · Strada Frate Emilio Chiocchetti - Lenz, n.7 int. 2 - 38035 Moena (TN) · Val di Fassa / Trentino / Dolomiti · P.IVA 02833250224 · Email: info@obsidianprivatedriver.it · PEC: obsidiantaxi@pec.it · Telefono: +39 375 116 3111.",
      en: "OBSIDIAN TAXI di Rech Nicky · Strada Frate Emilio Chiocchetti - Lenz, n.7 int. 2 - 38035 Moena (TN) · Val di Fassa / Trentino / Dolomites · VAT no. 02833250224 · Email: info@obsidianprivatedriver.it · PEC: obsidiantaxi@pec.it · Phone: +39 375 116 3111.",
    },
  },
  {
    title: { it: "15. Aggiornamento", en: "15. Update" },
    body: {
      it: "Queste condizioni sono state aggiornate alla data di pubblicazione del sito. Eventuali modifiche saranno pubblicate su questa pagina.",
      en: "These conditions were updated on the website publication date. Any changes will be published on this page.",
    },
  },
];

const PRIVACY = {
  it: [
    {
      h: "Titolare del trattamento",
      p: ["OBSIDIAN TAXI di Rech Nicky — Strada Frate Emilio Chiocchetti - Lenz, n.7 int. 2 - 38035 Moena (TN), Val di Fassa / Trentino / Dolomiti — P.IVA 02833250224 — Email: info@obsidianprivatedriver.it — PEC: obsidiantaxi@pec.it — Telefono: +39 375 116 3111."],
    },
    {
      h: "Quali dati raccogliamo",
      p: [
        "Dati forniti volontariamente tramite il form di richiesta informazioni: nome e cognome, indirizzo email, numero di telefono/WhatsApp, e i dettagli del viaggio comunicati (data, orario, partenza, destinazione, numero di passeggeri, bagagli, volo o treno, bambini, animali, attrezzatura speciale, messaggio).",
        "Dati di navigazione raccolti, esclusivamente previo consenso, tramite Google Analytics 4 in forma aggregata e anonima: pagine visitate, durata della visita, provenienza approssimativa, tipo di dispositivo. L'indirizzo IP è anonimizzato.",
        "Dati tecnici di navigazione (log del server) trattati per brevi periodi per finalità di sicurezza.",
      ],
    },
    {
      h: "Finalità e base giuridica",
      p: [
        "Gestione della richiesta di transfer o preventivo: esecuzione di misure precontrattuali o obblighi contrattuali (art. 6, par. 1, lett. b — GDPR). Il conferimento è necessario per gestire la richiesta.",
        "Analytics (Google Analytics 4) con cookie di terze parti: tuo consenso revocabile in ogni momento (art. 6, par. 1, lett. a — GDPR).",
        "Obblighi di legge contabili e amministrativi eventualmente applicabili: obbligo legale (art. 6, par. 1, lett. c — GDPR).",
      ],
    },
    {
      h: "Modalità del trattamento e conservazione",
      p: [
        "I dati sono trattati con strumenti elettronici nel rispetto del principio di minimizzazione, con misure adeguate di sicurezza. I dati delle richieste sono conservati per il tempo necessario a gestirle e, se il servizio viene confermato, per gli obblighi contabili; in mancanza, non oltre 12 mesi.",
        "I dati non sono oggetto di trasferimenti extra-UE né di decisioni automatizzate; Google Analytics 4 è configurato in forma anonima e aggregata.",
      ],
    },
    {
      h: "Comunicazione a terzi",
      p: [
        "I dati possono essere trattati da fornitori tecnici (hosting, invio email) in qualità di responsabili o esercenti autonomi. Non sono ceduti a terzi per finalità di marketing.",
      ],
    },
    {
      h: "I tuoi diritti (artt. 15-22 GDPR)",
      p: [
        "Puoi esercitare in ogni momento: accesso, rettifica, cancellazione, limitazione, portabilità, opposizione e revoca del consenso, scrivendo a info@obsidianprivatedriver.it. Hai inoltre il diritto di proporre reclamo al Garante per la protezione dei dati personali (www.garanteprivacy.it).",
      ],
    },
    {
      h: "Cookie",
      p: [
        "Il sito utilizza cookie tecnici necessari al funzionamento e, previo consenso espresso tramite il banner, cookie analitici di Google Analytics 4. Puoi modificare le tue preferenze in ogni momento tramite il link \"Gestisci preferenze cookie\" nel footer. Maggiori dettagli nella Cookie Policy.",
      ],
    },
  ],
  en: [
    {
      h: "Data controller",
      p: ["OBSIDIAN TAXI di Rech Nicky — Strada Frate Emilio Chiocchetti - Lenz, n.7 int. 2 - 38035 Moena (TN), Val di Fassa / Trentino / Dolomites — VAT no. 02833250224 — Email: info@obsidianprivatedriver.it — PEC: obsidiantaxi@pec.it — Phone: +39 375 116 3111."],
    },
    {
      h: "Which data we collect",
      p: [
        "Data voluntarily provided through the information request form: full name, email address, phone/WhatsApp number and the journey details you share (date, time, departure, destination, number of passengers, luggage, flight or train, children, pets, special equipment, message).",
        "Navigation data collected, subject to consent, through Google Analytics 4 in aggregate and anonymous form: pages visited, visit duration, approximate origin, device type. The IP address is anonymised.",
        "Technical navigation data (server logs) processed for short periods for security purposes.",
      ],
    },
    {
      h: "Purposes and legal basis",
      p: [
        "Handling your transfer request or quote: performance of pre-contractual measures or contractual obligations (art. 6(1)(b) GDPR). Providing the data is necessary to handle the request.",
        "Analytics (Google Analytics 4) with third-party cookies: your revocable consent at any time (art. 6(1)(a) GDPR).",
        "Any accounting and administrative legal obligations: legal duty (art. 6(1)(c) GDPR).",
      ],
    },
    {
      h: "Processing methods and retention",
      p: [
        "Data is processed electronically in compliance with the minimisation principle, with appropriate security measures. Request data is kept for the time needed to handle it and, if the service is confirmed, for accounting obligations; otherwise no longer than 12 months.",
        "Data is not subject to extra-EU transfers or automated decision-making; Google Analytics 4 is configured in anonymous and aggregate form.",
      ],
    },
    {
      h: "Third-party communication",
      p: ["Data may be processed by technical providers (hosting, email delivery) as processors or independent controllers. It is not sold to third parties for marketing purposes."],
    },
    {
      h: "Your rights (arts. 15-22 GDPR)",
      p: ["You may exercise at any time: access, rectification, erasure, restriction, portability, objection and withdrawal of consent, by writing to info@obsidianprivatedriver.it. You also have the right to lodge a complaint with the Italian Data Protection Authority (www.garanteprivacy.it)."],
    },
    {
      h: "Cookies",
      p: ["The website uses technical cookies necessary for its operation and, subject to express consent through the banner, Google Analytics 4 analytical cookies. You can change your preferences at any time via the \"Manage cookie preferences\" link in the footer. Further details in the Cookie Policy."],
    },
  ],
};

const COOKIES = {
  it: {
    intro: "Un cookie è un piccolo file di testo che i siti web salvano sul tuo dispositivo durante la navigazione. I cookie vengono poi reinvii al sito stesso alla visita successiva.",
    tech: {
      t: "Cookie tecnici (sempre attivi)",
      b: "Necessari al funzionamento del sito: memorizzano la lingua scelta e, in forma anonima, le tue preferenze sui cookie. Non richiedono consenso e non raccolgono dati personali per finalità commerciali.",
    },
    analytics: {
      t: "Cookie analitici di terze parti — Google Analytics 4 (previo consenso)",
      b: "Utilizziamo Google Analytics 4, servizio di Google LLC, esclusivamente per misurare in forma aggregata e anonima gli accessi, le visite e i dati di navigazione (pagine viste, durata, provenienza, dispositivo). Questi cookie sono installati solo se accetti il consenso analitico nel banner; in caso di rifiuto non viene caricato alcun script di Google. L'IP è anonimizzato. Per l'informativa di Google: policies.google.com/privacy.",
    },
    manage: {
      t: "Come gestire le preferenze",
      b: "Puoi accettare tutti i cookie, solo quelli necessari o revocare il consenso in ogni momento tramite il link \"Gestisci preferenze cookie\" nel footer del sito. Puoi inoltre gestire o cancellare i cookie dalle impostazioni del tuo browser.",
    },
  },
  en: {
    intro: "A cookie is a small text file that websites save on your device while you browse. Cookies are then sent back to the same website on your next visit.",
    tech: {
      t: "Technical cookies (always active)",
      b: "Necessary for the website to work: they store your chosen language and, anonymously, your cookie preferences. They do not require consent and do not collect personal data for commercial purposes.",
    },
    analytics: {
      t: "Third-party analytical cookies — Google Analytics 4 (with consent)",
      b: "We use Google Analytics 4, a service by Google LLC, solely to measure accesses, visits and navigation data (pages viewed, duration, origin, device) in aggregate and anonymous form. These cookies are installed only if you accept analytical consent in the banner; if you refuse, no Google script is loaded. The IP is anonymised. Google's notice: policies.google.com/privacy.",
    },
    manage: {
      t: "How to manage your preferences",
      b: "You can accept all cookies, only the necessary ones, or withdraw consent at any time via the \"Manage cookie preferences\" link in the website footer. You can also manage or delete cookies from your browser settings.",
    },
  },
};

export default function LegalPage({ kind }) {
  const { lang } = useLang();
  const L = T[lang];
  const titles = { termini: L.terminiTitle, privacy: L.privacyTitle, cookies: L.cookiesTitle };
  const intros = { termini: L.terminiIntro, privacy: L.privacyIntro, cookies: L.cookiesIntro };

  return (
    <main className="pt-36 pb-24 sm:pt-44" data-testid={`legal-page-${kind}`}>
      <div className="mx-auto max-w-3xl px-6 sm:px-10">
        <Reveal>
          <div className="mb-8 flex justify-center">
            <Wordmark />
          </div>
        </Reveal>
        <Reveal delay={0.05}>
          <Eyebrow className="justify-center">{L.eyebrow}</Eyebrow>
        </Reveal>
        <Reveal delay={0.1}>
          <h1 className="font-display mt-6 text-4xl font-light leading-tight text-[#FAF9F6] sm:text-5xl" data-testid="legal-title">
            {titles[kind]}
          </h1>
          <p className="mt-4 text-sm font-light text-[#A1A1AA]">{intros[kind]}</p>
        </Reveal>

        {kind === "termini" && (
          <div className="mt-14 space-y-10" data-testid="legal-terms-body">
            {CONDITIONS.map((c, i) => (
              <Reveal key={i} delay={Math.min(i * 0.03, 0.2)}>
                <article>
                  <h2 className="font-display text-xl font-light text-[#C5A059] sm:text-2xl">{c.title[lang] || c.title.it}</h2>
                  <p className="mt-3 text-[13px] font-light leading-relaxed text-[#A1A1AA] sm:text-sm">{c.body[lang] || c.body.it}</p>
                </article>
              </Reveal>
            ))}
          </div>
        )}

        {kind === "privacy" && (
          <div className="mt-14 space-y-10" data-testid="legal-privacy-body">
            {(PRIVACY[lang] || PRIVACY.it).map((s, i) => (
              <Reveal key={i} delay={Math.min(i * 0.03, 0.2)}>
                <article>
                  <h2 className="font-display text-xl font-light text-[#C5A059] sm:text-2xl">{s.h}</h2>
                  {s.p.map((p, j) => (
                    <p key={j} className="mt-3 text-[13px] font-light leading-relaxed text-[#A1A1AA] sm:text-sm">{p}</p>
                  ))}
                </article>
              </Reveal>
            ))}
          </div>
        )}

        {kind === "cookies" && (
          <div className="mt-14 space-y-10" data-testid="legal-cookies-body">
            <Reveal><p className="text-[13px] font-light leading-relaxed text-[#A1A1AA] sm:text-sm">{COOKIES[lang].intro}</p></Reveal>
            {[COOKIES[lang].tech, COOKIES[lang].analytics, COOKIES[lang].manage].map((s, i) => (
              <Reveal key={i} delay={0.05 * i}>
                <article className="border-l border-[#C5A059]/40 pl-6">
                  <h2 className="font-display text-xl font-light text-[#FAF9F6] sm:text-2xl">{s.t}</h2>
                  <p className="mt-3 text-[13px] font-light leading-relaxed text-[#A1A1AA] sm:text-sm">{s.b}</p>
                </article>
              </Reveal>
            ))}
          </div>
        )}

        <Reveal delay={0.1}>
          <div className="mt-16 flex flex-col gap-4 border-t hairline pt-8 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap gap-6 text-[10px] uppercase tracking-[0.3em]">
              <Link to="/termini" className={`transition-colors hover:text-[#C5A059] ${kind === "termini" ? "text-[#C5A059]" : "text-[#A1A1AA]"}`} data-testid="legal-nav-termini">Termini</Link>
              <Link to="/privacy" className={`transition-colors hover:text-[#C5A059] ${kind === "privacy" ? "text-[#C5A059]" : "text-[#A1A1AA]"}`} data-testid="legal-nav-privacy">Privacy</Link>
              <Link to="/cookie" className={`transition-colors hover:text-[#C5A059] ${kind === "cookies" ? "text-[#C5A059]" : "text-[#A1A1AA]"}`} data-testid="legal-nav-cookies">Cookie</Link>
            </div>
            <Link to="/" className="btn-outline !px-6 !py-3" data-testid="legal-back-home">{L.back}</Link>
          </div>
        </Reveal>
      </div>
    </main>
  );
}
